import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import LocationPermissionModal from '../components/LocationPermissionModal';

const LocationContext = createContext(null);

// Local storage key for tracking if user dismissed the prompt
const LOCATION_PROMPT_DISMISSED_KEY = 'vibary_location_prompt_dismissed';

// Geolocation options per documented standards
const GEOLOCATION_OPTIONS = {
  enableHighAccuracy: false,  // Battery efficient
  timeout: 10000,             // 10 second timeout
  maximumAge: 300000          // 5 minute cache
};

/**
 * Location Context Provider
 *
 * Manages user location with a pre-prompt modal following app standards:
 * - Shows pre-prompt explaining benefits before browser dialog
 * - Provides "Not Now" option (doesn't show again this session)
 * - Shows feedback if permission is denied
 * - Graceful degradation if location unavailable
 */
export function LocationProvider({ children }) {
  // Location state
  const [userLocation, setUserLocation] = useState(null);
  const [permissionState, setPermissionState] = useState('unknown'); // 'unknown' | 'granted' | 'denied' | 'prompt'
  const [isLoading, setIsLoading] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalShownThisSession, setModalShownThisSession] = useState(false);

  // Check if user previously dismissed the prompt
  const wasPromptDismissed = useCallback(() => {
    try {
      return sessionStorage.getItem(LOCATION_PROMPT_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  }, []);

  // Mark prompt as dismissed for this session
  const dismissPrompt = useCallback(() => {
    try {
      sessionStorage.setItem(LOCATION_PROMPT_DISMISSED_KEY, 'true');
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Check current permission state on mount
  useEffect(() => {
    const checkPermission = async () => {
      if (!navigator.permissions || !navigator.geolocation) {
        setPermissionState('denied');
        return;
      }

      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionState(result.state);

        // If already granted, get location immediately
        if (result.state === 'granted') {
          requestLocationSilently();
        }

        // Listen for permission changes
        result.addEventListener('change', () => {
          setPermissionState(result.state);
          if (result.state === 'granted') {
            requestLocationSilently();
          }
        });
      } catch {
        // Permissions API not supported, will prompt when needed
        setPermissionState('unknown');
      }
    };

    checkPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Request location without showing modal (for already-granted permission)
  const requestLocationSilently = useCallback(() => {
    if (!navigator.geolocation) return;

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setPermissionState('granted');
        setIsLoading(false);
      },
      (error) => {
        console.warn('Location error:', error.message);
        setPermissionState('denied');
        setIsLoading(false);
      },
      GEOLOCATION_OPTIONS
    );
  }, []);

  // Request location after user approves in pre-prompt modal
  const requestLocationWithPermission = useCallback(() => {
    if (!navigator.geolocation) {
      setPermissionState('denied');
      setShowModal(true); // Show denied state
      return;
    }

    setIsLoading(true);
    setShowModal(false);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setPermissionState('granted');
        setIsLoading(false);
      },
      (error) => {
        console.warn('Location permission denied:', error.message);
        setPermissionState('denied');
        setIsLoading(false);
        // Show modal with denied state and settings instructions
        setShowModal(true);
      },
      GEOLOCATION_OPTIONS
    );
  }, []);

  // Trigger location request with pre-prompt
  // Called by pages that need location (Map, ListDetail)
  const requestLocation = useCallback(() => {
    // Already have location
    if (userLocation) return;

    // Already granted, just get location
    if (permissionState === 'granted') {
      requestLocationSilently();
      return;
    }

    // Don't show modal if user dismissed it this session
    if (wasPromptDismissed() || modalShownThisSession) {
      return;
    }

    // Show pre-prompt modal
    setShowModal(true);
    setModalShownThisSession(true);
  }, [userLocation, permissionState, wasPromptDismissed, modalShownThisSession, requestLocationSilently]);

  // Handle modal close (Not Now)
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    dismissPrompt();
  }, [dismissPrompt]);

  // Handle enable location button
  const handleEnableLocation = useCallback(() => {
    requestLocationWithPermission();
  }, [requestLocationWithPermission]);

  const value = {
    userLocation,
    permissionState,
    isLoading,
    requestLocation,
    // Expose for components that need to know if location is available
    hasLocation: !!userLocation,
    isLocationDenied: permissionState === 'denied',
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
      <LocationPermissionModal
        isOpen={showModal}
        onClose={handleModalClose}
        onEnable={handleEnableLocation}
        isDenied={permissionState === 'denied'}
      />
    </LocationContext.Provider>
  );
}

/**
 * Hook to access location context
 */
export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}

export default LocationContext;
